import React, { type ChangeEvent, useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, MenuItem, Select, type SelectChangeEvent, Stack } from "@mui/material";
import { debounce } from "lodash";

import { useNewsFeed } from "../context/NewsFeedContext";

const SEARCH_DEBOUNCE_MS = 500;

const filterSelectSx = (active: boolean) =>
  ({
    height: 26,
    bgcolor: "#fff",
    borderRadius: "6px",
    fontSize: 12,
    fontWeight: 500,
    color: active ? "#26a6ae" : "#5d7782",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: active ? "#26a6ae" : "#dbe8f5"
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: active ? "#26a6ae" : "#dbe8f5"
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: active ? "#26a6ae" : "#dbe8f5"
    },
    "& .MuiSelect-select": {
      py: 0,
      pl: "9px",
      pr: "26px !important",
      minHeight: "24px",
      lineHeight: "24px",
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    "& .MuiSelect-icon": {
      fontSize: 18,
      color: "#5d7782",
      right: 4
    }
  }) as const;

const filterMenuItemSx = {
  fontSize: 12,
  py: "4px",
  minHeight: 0,
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
} as const;

export const NewsFeedFilters: React.FC = () => {
  const {
    feedConfig: {
      topics: { user: userTopics, system: systemTopics },
      sources: { user: userSources, system: systemSources }
    },
    // dealsCoverage,
    filters,
    setFilters,
    resetFilters
  } = useNewsFeed();

  // const filterDeals = useMemo(() => dealsCoverage.filter((deal) => deal.tracked), [dealsCoverage]);

  const availableTopics = useMemo(() => {
    if (userTopics.length) {
      return userTopics;
    }
    return systemTopics;
  }, [userTopics, systemTopics]);

  const availableSources = useMemo(() => {
    if (userSources.length) {
      return userSources;
    }
    return systemSources;
  }, [userSources, systemSources]);

  const hasFilters = Object.values(filters).some(Boolean);

  const [localSearch, setLocalSearch] = useState(filters.search);

  useEffect(() => {
    setLocalSearch((curr) => (curr === filters.search ? curr : filters.search));
  }, [filters.search]);

  const debouncedSetSearch = useMemo(
    () => debounce((search: string) => setFilters((filters) => ({ ...filters, search })), SEARCH_DEBOUNCE_MS),
    [setFilters]
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="7px"
      sx={{
        py: "9px",
        px: "20px",
        display: "flex",
        borderBottom: 1,
        borderColor: "#dbe8f5",
        bgcolor: "#fff",
        flexShrink: 0,
        borderRadius: "0px 0px 8px 8px"
      }}
    >
      {/* <Select
        displayEmpty
        value={filters.type}
        onChange={(e: SelectChangeEvent<string>) => setFilters((filters) => ({ ...filters, type: e.target.value }))}
        renderValue={(value) => (value === "deal" ? "Deal news" : value === "topic" ? "Topic news" : "All types")}
        sx={{ ...filterSelectSx(Boolean(filters.type)), minWidth: 108 }}
      >
        <MenuItem value="" sx={filterMenuItemSx}>
          All types
        </MenuItem>
        <MenuItem value="deal" sx={filterMenuItemSx}>
          Deal news
        </MenuItem>
        <MenuItem value="topic" sx={filterMenuItemSx}>
          Topic news
        </MenuItem>
      </Select> */}
      <Select
        displayEmpty
        value={filters.topic}
        onChange={(e: SelectChangeEvent<string>) => setFilters((filters) => ({ ...filters, topic: e.target.value }))}
        renderValue={(value) => (value ? value : "All topics")}
        sx={{ ...filterSelectSx(Boolean(filters.topic)), width: "150px !important" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 400, width: "150px !important" } } }}
      >
        <MenuItem value="" sx={filterMenuItemSx}>
          All topics
        </MenuItem>
        {availableTopics.map((t) => (
          <MenuItem key={t.id} value={String(t.title)} sx={filterMenuItemSx}>
            {t.title}
          </MenuItem>
        ))}
      </Select>
      <Select
        displayEmpty
        value={filters.source}
        onChange={(e: SelectChangeEvent<string>) => setFilters((filters) => ({ ...filters, source: e.target.value }))}
        renderValue={(value) => (value ? value : "All sources")}
        sx={{ ...filterSelectSx(Boolean(filters.source)), width: "150px !important" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 400, width: "150px !important" } } }}
      >
        <MenuItem value="" sx={filterMenuItemSx}>
          All sources
        </MenuItem>
        {availableSources.map((s) => (
          <MenuItem key={s.source_url} value={String(s.name)} sx={filterMenuItemSx}>
            {s.name}
          </MenuItem>
        ))}
      </Select>
      {/* <Select
        displayEmpty
        value={filters.deal}
        onChange={(e: SelectChangeEvent<string>) => setFilters((filters) => ({ ...filters, deal: e.target.value }))}
        renderValue={(value) => value || "All deals"}
        sx={{ ...filterSelectSx(Boolean(filters.deal)), minWidth: 160 }}
      >
        <MenuItem value="" sx={filterMenuItemSx}>
          All deals
        </MenuItem>
        {filterDeals.map((deal) => (
          <MenuItem key={deal.id} value={deal.title} sx={filterMenuItemSx}>
            {deal.title}
          </MenuItem>
        ))}
      </Select> */}
      <Box
        component="input"
        type="search"
        placeholder="Search headlines …"
        value={localSearch}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setLocalSearch(e.target.value);
          debouncedSetSearch(e.target.value);
        }}
        sx={{
          height: 26,
          flex: "1 1 180px",
          maxWidth: 180,
          border: "1px solid",
          borderColor: "#dbe8f5",
          borderRadius: "6px",
          pl: "9px",
          pr: "9px",
          fontSize: 12,
          color: "text.primary",
          bgcolor: "#fff",
          fontFamily: "inherit",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "left 7px center",
          "&::placeholder": { color: "#92a7bc", opacity: 1 },
          "&:focus": {
            outline: "2px solid rgba(7,31,53,0.12)",
            outlineOffset: 0
          }
        }}
      />
      <Button
        disabled={!hasFilters}
        onClick={resetFilters}
        variant="text"
        size="sm"
        startIcon={<CloseIcon sx={{ width: "16px", height: "16px" }} />}
        sx={{
          color: "#5d7782",
          "&:hover": {
            color: "#26a6ae"
          }
        }}
      >
        Clear
      </Button>
    </Stack>
  );
};
